import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { toast } from '@leadsnavi/ui-kit';
import {
  reportDetailGet,
  saveReportPost,
  unsaveReportDelete,
  followPost,
  unfollowPost,
  refollowPost,
} from '@/services/swagger/easourcing/ReportService';
import { navEmitter } from '@/utils/nav-emitter';
import { PagePath } from '@/routes/paths';
import { ReportsTabParam, getReportsTabPath } from '@/features/reports/constants';
import type { MarketInsightState } from './store-types';
import { transformApiReportDetail } from '../utils/parse-report-detail';
import useMarketInsightPanelStore from '@/features/chat-messages/store/market-insight-panel-store';

// ─────────────────────────────────────────────
// Store 初始化参数类型
// ─────────────────────────────────────────────

interface StoreInitPayload {
  /** 报告唯一 ID（字符串，后端下发） */
  reportId: string;
  /** 报告标题（卡片骨架展示，详情加载后以 API 数据为准） */
  title: string;
  /** 报告类型字符串，如 "MARKET_TREND" / "COMPANY_AUDIT" */
  type: string;
  /** 详情加载状态变更回调，供外层同步全局 loading 状态；reportId 为当前报告 ID 字符串 */
  onDetailLoadingChange?: (loading: boolean, reportId: string) => void;
}

const navigateToReportsTab = (path: string) => {
  navEmitter.emit('navigate', {
    path,
  });
  setTimeout(() => {
    useMarketInsightPanelStore.setState({ activeStore: null });
  });
};
// ─────────────────────────────────────────────
// Store 工厂函数
// ─────────────────────────────────────────────

/**
 * 创建 per-card 非全局 MarketInsightStore
 *
 * Store 随卡片组件生命周期创建和销毁（组件卸载后自动 GC），不污染全局状态。
 * 所有展示字段（title/summary/reportType/isSaved/followStatus 等）均从 detail 读取；
 * detail 未加载前，subMsgPayload 作为骨架态兜底数据。
 */
export function createMarketInsightStore(payload: StoreInitPayload) {
  return create<MarketInsightState>()(
    immer((set, get) => ({
      // ── subMsg payload（只读，来自消息流初始化）──
      subMsgPayload: payload,

      // ── 封面图（fetchDetail 后填充，API 附加字段）──
      coverImage: '',

      // ── 可变交互状态（乐观更新专用，独立于 detail）──
      saveLoading: false,
      followLoading: false,

      // ── 报告详情 ──
      detail: null,
      detailLoading: false,
      detailError: null,
      isDetailOpen: false,

      // ── Actions ──

      /**
       * 切换收藏状态（乐观更新 + 失败回滚）
       * reportId / isSaved 均从 detail 读取
       */
      toggleSave: async () => {
        const { saveLoading, detail } = get();
        if (saveLoading || !detail) return;

        const reportId = Number(detail.reportId) || 0;
        const prevIsSaved = detail.isSaved === 1;

        // 乐观更新：detail.isSaved 取反
        set(s => {
          s.saveLoading = true;
          if (s.detail) {
            s.detail.isSaved = prevIsSaved ? 0 : 1;
          }
        });

        try {
          if (prevIsSaved) {
            await unsaveReportDelete({ reportId });
          } else {
            await saveReportPost({ reportId });
            toast.success(
              <span>
                Report saved to{' '}
                <button
                  className="underline cursor-pointer font-medium"
                  onClick={() => {
                    navigateToReportsTab(getReportsTabPath(ReportsTabParam.Saved, PagePath.reports));
                  }}
                >
                  Saved Reports
                </button>{' '}
                under the Reports page.
              </span>
            );
          }
        } catch (error) {
          // 失败回滚
          set(s => {
            if (s.detail) {
              s.detail.isSaved = prevIsSaved ? 1 : 0;
            }
          });
          console.error('[MarketInsightStore] toggleSave failed:', error);
        } finally {
          set(s => {
            s.saveLoading = false;
          });
        }
      },

      /**
       * 切换关注状态
       * followStatus / topicId 均从 detail.topic 读取
       * followStatus=0 → followPost → 1
       * followStatus=1 → unfollowPost → 2
       * followStatus=2 → refollowPost → 1
       */
      toggleFollow: async () => {
        const { followLoading, detail } = get();
        if (followLoading || !detail) return;

        const topicId = Number(detail.topic?.topicId) || 0;
        const followStatus = (detail.topic?.followStatus ?? 0) as 0 | 1 | 2;

        set(s => {
          s.followLoading = true;
        });

        const setFollowStatus = (status: 0 | 1 | 2) =>
          set(s => {
            if (s.detail?.topic) {
              s.detail.topic.followStatus = status;
            }
          });

        try {
          if (followStatus === 0) {
            await followPost({ topicId });
            setFollowStatus(1);
            toast.success(
              <span>
                This topic will be tracked for you regularly in{' '}
                <button
                  className="underline cursor-pointer font-medium"
                  onClick={() => {
                    navigateToReportsTab(getReportsTabPath(ReportsTabParam.Following, PagePath.reports));
                  }}
                >
                  Following
                </button>{' '}
                under the Reports page.
              </span>
            );
          } else if (followStatus === 1) {
            await unfollowPost({ topicId });
            setFollowStatus(2);
            toast.success(
              <span>
                The topic can be viewed on{' '}
                <button
                  className="underline cursor-pointer font-medium"
                  onClick={() => {
                    navigateToReportsTab(getReportsTabPath(ReportsTabParam.Unfollowed, PagePath.reports));
                  }}
                >
                  unfollowed
                </button>
              </span>
            );
          } else {
            await refollowPost({ topicId });
            setFollowStatus(1);
            toast.success(
              <span>
                This topic will be tracked for you regularly in{' '}
                <button
                  className="underline cursor-pointer font-medium"
                  onClick={() => {
                    navigateToReportsTab(getReportsTabPath(ReportsTabParam.Following, PagePath.reports));
                  }}
                >
                  Following
                </button>{' '}
                under the Reports page.
              </span>
            );
          }
        } catch (error) {
          // 失败回滚
          setFollowStatus(followStatus);
          console.error('[MarketInsightStore] toggleFollow failed:', error);
        } finally {
          set(s => {
            s.followLoading = false;
          });
        }
      },

      /**
       * 加载报告详情（防重复调用）
       * 成功后以 detail 承载所有展示字段（title/summary/isSaved/followStatus 等）
       */
      fetchDetail: async () => {
        const { subMsgPayload, detailLoading } = get();
        if (detailLoading) return;

        const reportId = Number(subMsgPayload.reportId) || 0;
        payload.onDetailLoadingChange?.(true, subMsgPayload.reportId);
        set(s => {
          s.detailLoading = true;
          s.detailError = null;
        });

        try {
          const result = await reportDetailGet({ reportId });
          const respData = result?.data;
          if (!respData) {
            set(s => {
              s.detailError = '数据加载失败，请重试';
              s.detailLoading = false;
            });
            payload.onDetailLoadingChange?.(false, subMsgPayload.reportId);
            return;
          }

          const detail = transformApiReportDetail(respData);
          const coverImage = ((respData as unknown as Record<string, unknown>).coverImage as string | undefined) ?? '';

          set(s => {
            s.detail = detail;
            s.coverImage = coverImage;
            s.detailLoading = false;
          });
          payload.onDetailLoadingChange?.(false, subMsgPayload.reportId);
        } catch (error) {
          set(s => {
            s.detailError = '数据加载失败，请重试';
            s.detailLoading = false;
          });
          payload.onDetailLoadingChange?.(false, subMsgPayload.reportId);
          console.error('[MarketInsightStore] fetchDetail failed:', error);
        }
      },

      /**
       * 打开/关闭详情面板
       * open=true 且 detail=null 时自动触发 fetchDetail()
       */
      setDetailOpen: (open: boolean) => {
        set(s => {
          s.isDetailOpen = open;
        });
        if (open && get().detail === null) {
          get().fetchDetail();
        }
      },
    }))
  );
}

export type MarketInsightStore = ReturnType<typeof createMarketInsightStore>;
